import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth, useClerk } from '@clerk/clerk-react'
import {
  verifySuperAdmin,
  type AdminCheckResult,
} from '../../../src/lib/super-admin-gate'

/**
 * Verifies the signed-in Clerk user is the runtime SUPER_ADMIN before
 * rendering any staff UI. See TMA-337 — the runtime + product apps share
 * a Clerk session via the parent-domain cookie, so we cannot rely on
 * Clerk auth alone to keep customers out of the staff app.
 *
 * Mount INSIDE Clerk's <SignedIn /> so we know a session exists. While
 * the check is in flight we render only a centered spinner — never the
 * runtime shell — to eliminate any flash of admin UI.
 */
export function SuperAdminGate({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useAuth()
  const { signOut } = useClerk()
  const [state, setState] = useState<AdminCheckResult | 'checking'>('checking')
  const attemptRef = useRef(0)

  const runCheck = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return
    setState('checking')
    const result = await verifySuperAdmin({
      apiBase: import.meta.env.VITE_API_URL ?? '',
      getToken: () => getToken(),
      signOut: () => signOut(),
      redirect: (url) => { window.location.href = url },
    })
    setState(result)
  }, [isLoaded, isSignedIn, getToken, signOut])

  useEffect(() => {
    void runCheck()
  }, [runCheck])

  if (!isLoaded || state === 'checking' || state === 'rejected') {
    return <CenteredSpinner />
  }

  if (state === 'error') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-cmd-base px-4 text-center text-white/90">
        <p className="text-sm font-medium">Unable to verify access</p>
        <p className="text-xs text-cmd-outline">
          We couldn&rsquo;t reach the runtime API to confirm your account.
          Check your connection and try again.
        </p>
        <button
          type="button"
          className="rounded border border-cmd-outline px-3 py-1.5 text-xs uppercase tracking-widest text-cmd-outline hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          onClick={() => {
            attemptRef.current += 1
            void runCheck()
          }}
        >
          Retry
        </button>
      </div>
    )
  }

  return <>{children}</>
}

function CenteredSpinner() {
  return (
    <div
      role="status"
      aria-label="Verifying access"
      className="flex min-h-screen items-center justify-center bg-cmd-base"
    >
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-cmd-outline border-t-primary" />
    </div>
  )
}
